import { assignUserRole } from "../../api/roles.js";
import { STORAGE_KEYS } from "../../utils/constants";
import { finalizeLoginSession } from "../../api/client";
import { syncLocalStorageToBackend } from "../../api/borrowerPortal/shortlistedModal.js";
import {
  shortlistPathForCategory,
  sourcingPathForCategory,
  readRememberedSourcingCategory,
} from "../../utils/sourcingCategory.js";

export { hasDirectLoginPayload } from "../../utils/loginAuth.js";

const LS_PENDING = STORAGE_KEYS.SHORTLIST_PENDING;
const LS_SHORTLIST = STORAGE_KEYS.SHORTLIST;
const LS_DRAFT_IDS = STORAGE_KEYS.SHORTLIST_DRAFT_IDS;
const LS_POST_AUTH = STORAGE_KEYS.POST_AUTH_REDIRECT;

function safeReadArray(key) {
  try {
    const raw = localStorage.getItem(key);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function finalizeShortlistAfterAuth() {
  const pending = safeReadArray(LS_PENDING);
  if (pending.length) {
    try {
      localStorage.setItem(LS_SHORTLIST, JSON.stringify(pending.slice(0, 5)));
    } catch (e) {
      console.error("❌ [finalizeShortlistAfterAuth] Failed to save:", e);
    }
  }
  try {
    localStorage.removeItem(LS_PENDING);
    localStorage.removeItem(LS_DRAFT_IDS);
  } catch { /* ignore */ }
}

export function getPostAuthRedirect(location) {
  try {
    const params = new URLSearchParams(location.search);
    const next = params.get("next") || location.state?.next;
    if (next) return next;

    const to = localStorage.getItem(LS_POST_AUTH);
    if (to) {
      localStorage.removeItem(LS_POST_AUTH);
      if (String(to).startsWith("/borrower/shortlisted") && !String(to).includes("category=")) {
        return shortlistPathForCategory();
      }
      return to;
    }

    const shortlist = safeReadArray(LS_SHORTLIST);
    if (shortlist.length > 0) return shortlistPathForCategory();
  } catch { /* ignore */ }
  return "/borrower/dashboard";
}

function hasExplicitNext(location) {
  try {
    return Boolean(new URLSearchParams(location.search).get("next") || location.state?.next);
  } catch {
    return false;
  }
}

/**
 * @param {object} [options]
 * @param {boolean} [options.viaGoogle] Google sign-in/sign-up: with no guest shortlist, land on sourcing.
 * @param {string} [options.sourcingCategory] e.g. "BTL" / "IF"; defaults to the last sourcing page visited.
 */
export async function completeBorrowerLogin(
  em,
  response,
  payload,
  remember,
  navigate,
  location,
  password = null,
  options = {}
) {
  const hadGuestShortlist = safeReadArray(LS_PENDING).length > 0;

  await finalizeLoginSession({
    email: em,
    password,
    loginResponse: response,
    loginPayload: payload,
    remember,
    requireCredentials: true,
  });

  // Same as custom_signup: ensure Borrower role.
  // Prefer /api/assign-role (FRAPPE_AUTH) — Google Website Users cannot PUT User.roles.
  let roleResult = await assignUserRole(em, "Borrower");
  if (!roleResult.ok) {
    await new Promise((r) => setTimeout(r, 500));
    roleResult = await assignUserRole(em, "Borrower");
  }
  if (roleResult.ok) {
    console.log("✅ Borrower role assigned for", em);
  } else {
    console.warn("⚠️ Assign Borrower role:", roleResult.error);
  }

  const storage = remember ? localStorage : sessionStorage;
  storage.setItem(STORAGE_KEYS.IS_AUTHED, "1");
  storage.setItem(STORAGE_KEYS.PORTAL, "borrower");
  if (STORAGE_KEYS.BORROWER_EMAIL) {
    storage.setItem(STORAGE_KEYS.BORROWER_EMAIL, em);
  } else {
    storage.setItem("BORROWER_EMAIL", em);
  }

  finalizeShortlistAfterAuth();

  try {
    const syncResult = await syncLocalStorageToBackend();
    if (syncResult.synced > 0) {
      console.log(`✅ Synced ${syncResult.synced} shortlisted products to backend`);
      await new Promise((resolve) => setTimeout(resolve, 2000));
    }
    if (syncResult.shortlistFull) {
      try {
        sessionStorage.setItem("ds_shortlist_full_warning", "1");
      } catch { /* ignore */ }
    }
    if (syncResult.errors.length > 0) {
      console.warn("Some products failed to sync:", syncResult.errors);
    }
  } catch (syncErr) {
    console.warn("Failed to sync shortlist to backend:", syncErr);
  }

  let redirectTo = getPostAuthRedirect(location);
  if (options.viaGoogle && !hadGuestShortlist && !hasExplicitNext(location)) {
    redirectTo = sourcingPathForCategory(
      options.sourcingCategory || readRememberedSourcingCategory()
    );
  }
  navigate(redirectTo, { replace: true });
}
